import { 
  API_CONFIG, 
  buildWCUrl, 
  getDefaultHeaders,
  type APIResponse
} from '../../config/api.enhanced';

// Types for payments
export interface PaymentMethod {
  id: string;
  title: string;
  description: string;
  descriptionHtml?: string;
  icon: string;
  order_button_text: string;
  type: 'cash_on_delivery' | 'bank_transfer' | 'paypal' | 'credit_card' | 'online_payment';
  has_fields: boolean;
  supports: {
    products: boolean;
    refunds: boolean;
    subscriptions: boolean;
    tokenization: boolean;
  };
  instructions?: string;
  account_details?: any[];
  settings?: {
    min_amount?: number;
    max_amount?: number;
  };
}

export interface PaymentMethodsResponse {
  success: boolean;
  payment_methods: PaymentMethod[];
  total: number;
  country: string;
  order_total: number;
  currency: string;
  currency_symbol: string;
  language: string;
  messages: {
    select_payment: string;
    payment_secure: string;
    no_methods: string;
    fees_may_apply: string;
    redirect_notice: string;
  };
  timestamp: string;
}

export interface PaymentInstructions {
  title: string;
  description: string;
  type: string;
  steps?: string[];
  account_details?: any[];
  order_reference?: string;
  total_amount?: string;
}

export interface ProcessPaymentData {
  orderId: number;
  paymentMethodId: string;
  paymentData?: {
    card_number?: string;
    expiry_month?: string;
    expiry_year?: string;
    cvc?: string;
    cardholder_name?: string;
  };
  return_url?: string;
  cancel_url?: string;
}

export interface PaymentResult {
  success: boolean;
  payment_id?: string;
  transaction_id?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  payment_url?: string;
  redirect_url?: string;
  message: string;
  receipt_url?: string;
}

/**
 * Payment Service
 * Handles all payment-related API calls
 */
class PaymentService {
  private language: string = API_CONFIG.DEFAULT_LANGUAGE;

  setLanguage(lang: string): void {
    if (API_CONFIG.SUPPORTED_LANGUAGES.includes(lang)) {
      this.language = lang;
    }
  }

  private async request<T = any>(
    url: string,
    options: RequestInit = {},
    useAuth: boolean = false
  ): Promise<APIResponse<T>> {
    try {
      const headers = {
        ...getDefaultHeaders(),
        ...options.headers,
      };

      if (useAuth) {
        const token = localStorage.getItem('auth_token');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

const response = await fetch(url, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.message || `HTTP ${response.status}: ${response.statusText}`,
          data: data
        };
      }

      return {
        success: true,
        data,
        message: data.message,
        language: this.language,
        timestamp: new Date().toISOString()
      };

    } catch (error) {

      return {
        success: false,
        message: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Get available payment methods
   */
  async getPaymentMethods(
    orderTotal: number = 0,
    country: string = 'SA'
  ): Promise<APIResponse<PaymentMethodsResponse>> {
    try {

const url = buildWCUrl(API_CONFIG.ENDPOINTS.PAYMENT_GATEWAYS, { 
        lang: this.language
      });
      
      const response = await this.request(url, { method: 'GET' }, false);
      const paymentGateways = this.extractPaymentGateways(response);
      
      if (!paymentGateways) {
        throw new Error('Invalid response format');
      }
      
      const transformedResponse = this.transformPaymentGateways(
        paymentGateways, 
        orderTotal, 
        country
      );
      
      return {
        success: true,
        data: transformedResponse,
        message: 'Payment methods loaded successfully'
      };
      
    } catch (error) {

      return this.getMockPaymentMethods(orderTotal, country);
    }
  }

  private extractPaymentGateways(response: any): any[] | null {
    let paymentGateways: any[] = [];
    
    if (response && response.success && Array.isArray(response.data)) {

      paymentGateways = response.data;
    } else if (response && Array.isArray(response)) {

      paymentGateways = response;
    } else {

      return null;
    }

return paymentGateways;
  }

  private transformPaymentGateways(
    paymentGateways: any[], 
    orderTotal: number, 
    country: string
  ): PaymentMethodsResponse {
    // Filter only enabled gateways and transform to our format
    const enabledGateways = paymentGateways.filter((gateway: any) => gateway.enabled === true);

    const transformedMethods: PaymentMethod[] = enabledGateways.map((gateway: any) => {
      const meta = gateway?.sbb_meta ?? {};
      const descriptionHtml = this.pickGatewayDescriptionHtml(gateway, meta);
      const descriptionText = this.pickGatewayDescriptionText(meta, descriptionHtml, gateway);
      const iconHtml = typeof meta.icon_html === 'string' ? meta.icon_html.trim() : '';
      const iconUrl = typeof meta.icon_url === 'string' ? meta.icon_url.trim() : '';
      const fallbackIcon = this.extractIconFromDescription(descriptionHtml || gateway.description || '');
      const instructions = this.pickGatewayInstructions(gateway, meta);
      const accountDetails = this.pickGatewayAccountDetails(gateway, meta);
      const methodSupports: string[] = Array.isArray(gateway.method_supports) ? gateway.method_supports : [];

      return {
        id: gateway.id,
        title: this.pickGatewayTitle(gateway, meta),
        description: descriptionText,
        descriptionHtml: descriptionHtml || undefined,
        icon: iconHtml || iconUrl || fallbackIcon || '',
        order_button_text: this.language === 'ar' ? 'ادفع الآن' : 'Pay Now',
        type: this.determinePaymentType(gateway.id),
        has_fields: false,
        supports: {
          products: methodSupports.length === 0 ? true : methodSupports.includes('products'),
          refunds: methodSupports.includes('refunds'),
          subscriptions: methodSupports.includes('subscriptions'),
          tokenization: methodSupports.includes('tokenization')
        },
        ...(instructions ? { instructions } : {}),
        ...(accountDetails ? { account_details: accountDetails } : {})
      };
    });

    return {
      success: true,
      payment_methods: transformedMethods,
      total: transformedMethods.length,
      country: country,
      order_total: orderTotal,
      currency: 'SAR',
      currency_symbol: 'ر.س',
      language: this.language,
      messages: {
        select_payment: this.language === 'ar' ? 'اختر طريقة الدفع' : 'Select Payment Method',
        payment_secure: this.language === 'ar' ? 'جميع المدفوعات آمنة ومحمية' : 'All payments are secure and protected',
        no_methods: this.language === 'ar' ? 'لا توجد طرق دفع متاحة' : 'No payment methods available',
        fees_may_apply: this.language === 'ar' ? 'قد تطبق رسوم إضافية' : 'Additional fees may apply',
        redirect_notice: this.language === 'ar' ? 'سيتم توجيهك لإتمام الدفع' : 'You will be redirected to complete payment'
      },
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Get payment method details by ID
   */
  async getPaymentMethodDetails(
    methodId: string
  ): Promise<APIResponse<PaymentMethod>> {
    try {
      const url = buildWCUrl(`${API_CONFIG.ENDPOINTS.PAYMENT_METHOD}/${methodId}`, { lang: this.language });
      return this.request(url, { method: 'GET' }, false);
    } catch (error) {

// Fallback: Return mock method details
      const mockMethod: PaymentMethod = {
        id: methodId,
        title: this.language === 'ar' ? 'طريقة دفع افتراضية' : 'Default Payment Method',
        description: this.language === 'ar' ? 'طريقة دفع متاحة' : 'Available payment method',
        icon: '',
        order_button_text: this.language === 'ar' ? 'ادفع الآن' : 'Pay Now',
        type: 'online_payment',
        has_fields: false,
        supports: {
          products: true,
          refunds: false,
          subscriptions: false,
          tokenization: false
        }
      };

      return {
        success: true,
        data: mockMethod,
        message: 'Payment method details loaded (local fallback)'
      };
    }
  }

  /**
   * Get payment instructions for a method
   */
  async getPaymentInstructions(
    methodId: string,
    orderId?: number
  ): Promise<APIResponse<PaymentInstructions>> {
    try {
      const params: any = { lang: this.language };
      if (orderId) params.order_id = orderId.toString();
      
      const url = buildWCUrl(`${API_CONFIG.ENDPOINTS.PAYMENT_INSTRUCTIONS}/${methodId}`, params);
      return this.request(url, { method: 'GET' }, false);
    } catch (error) {

// Fallback: Return mock instructions
      const mockInstructions: PaymentInstructions = {
        title: this.language === 'ar' ? 'تعليمات الدفع' : 'Payment Instructions',
        description: this.language === 'ar' ? 'اتبع هذه الخطوات لإتمام الدفع' : 'Follow these steps to complete payment',
        type: methodId,
        steps: this.language === 'ar' ? [
          '1. احجز رحلتك الآن',
          '2. أكمل عملية الدفع',
          '3. ستحصل على تأكيد الحجز'
        ] : [
          '1. Book your trip now',
          '2. Complete the payment process',
          '3. You will receive booking confirmation'
        ]
      };

      return {
        success: true,
        data: mockInstructions,
        message: 'Payment instructions loaded (local fallback)'
      };
    }
  }

  /**
   * Process payment for an order
   */
  async processPayment(paymentData: ProcessPaymentData): Promise<APIResponse<PaymentResult>> {
    try {
      // Use the unified boat order endpoint; backend will branch by payment_method
      const url = buildWCUrl(API_CONFIG.ENDPOINTS.BOAT_ORDER);

return this.request(url, {
        method: 'POST',
        body: JSON.stringify(paymentData)
      }, true);
    } catch (error) {

return {
        success: false,
        message: this.language === 'ar' 
          ? 'فشل في معالجة الدفع. يرجى المحاولة مرة أخرى.' 
          : 'Payment processing failed. Please try again.'
      };
    }
  }

  // Helper methods
  private extractIconFromDescription(description: string): string {
    if (!description) return '';
    
    // Look for img tags in description
    const imgMatch = description.match(/<img[^>]+src=["']([^"']+)["'][^>]*>/i);
    if (imgMatch) {
      return imgMatch[1];
    }
    
    return '';
  }

  private pickGatewayTitle(gateway: any, meta: any): string {
    const candidates = [
      meta.display_title,
      gateway.title,
      gateway.method_title,
      gateway.method_description,
      gateway.id
    ];
    const chosen = candidates.find(value => typeof value === 'string' && value.trim().length > 0);
    return chosen ? this.stripHtml(chosen) : '';
  }

  private pickGatewayDescriptionHtml(gateway: any, meta: any): string {
    const candidates = [
      meta.display_description_html,
      gateway.description,
      gateway.method_description
    ];
    const chosen = candidates.find(value => typeof value === 'string' && value.trim().length > 0);
    return chosen || '';
  }

  private pickGatewayDescriptionText(meta: any, descriptionHtml: string, gateway: any): string {
    if (typeof meta.display_description_text === 'string' && meta.display_description_text.trim().length > 0) {
      return meta.display_description_text.trim();
    }

    const fallback = descriptionHtml || gateway.description || gateway.method_description || '';
    return this.stripHtml(fallback);
  }

  private pickGatewayInstructions(gateway: any, meta: any): string | undefined {
    const candidates = [
      meta.instructions,
      gateway.instructions,
      gateway.settings?.instructions?.value,
      gateway.settings?.instructions
    ];

    const chosen = candidates.find(value => typeof value === 'string' && value.trim().length > 0);
    return chosen ? chosen.trim() : undefined;
  }

  private pickGatewayAccountDetails(gateway: any, meta: any): any[] | undefined {
    const candidates = [
      meta.account_details,
      gateway.account_details,
      gateway.settings?.account_details?.value,
      gateway.settings?.account_details,
      gateway.settings?.accounts?.value,
      gateway.settings?.accounts,
    ];

    const chosen = candidates.find((value: any) => {
      if (!value) return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'string') return value.trim().length > 0;
      if (typeof value === 'object') return true;
      return false;
    });

    if (!chosen) return undefined;

    if (Array.isArray(chosen)) {
      return chosen;
    }

    // Some gateways wrap values like { value: [...] }
    if (typeof chosen === 'object' && Array.isArray((chosen as any).value)) {
      return (chosen as any).value;
    }

    // Sometimes stored as JSON string
    if (typeof chosen === 'string') {
      const trimmed = chosen.trim();
      if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return parsed;
          if (parsed && Array.isArray((parsed as any).value)) return (parsed as any).value;
        } catch {
          return undefined;
        }
      }
    }

    return undefined;
  }

  private stripHtml(content?: string): string {
    if (!content || typeof content !== 'string') {
      return '';
    }
    const withoutTags = content.replace(/<[^>]*>/g, ' ');
    return withoutTags.replace(/\s+/g, ' ').trim();
  }

  private determinePaymentType(gatewayId: string): PaymentMethod['type'] {
    switch (gatewayId.toLowerCase()) {
      case 'cod':
        return 'cash_on_delivery';
      case 'bacs':
        return 'bank_transfer';
      case 'cheque':
        return 'bank_transfer';
      case 'tap':
      case 'stripe':
        return 'credit_card';
      case 'paypal':
        return 'paypal';
      default:
        return 'online_payment';
    }
  }

  private getMockPaymentMethods(orderTotal: number, country: string): APIResponse<PaymentMethodsResponse> {
    const mockMethods: PaymentMethod[] = [
      {
        id: 'cod',
        title: this.language === 'ar' ? 'الدفع عند الاستلام' : 'Cash on Delivery',
        description: this.language === 'ar' ? 'ادفع نقداً عند الصعود للقارب' : 'Pay cash when boarding the boat',
        icon: '',
        order_button_text: this.language === 'ar' ? 'احجز الآن - ادفع لاحقاً' : 'Book Now - Pay Later',
        type: 'cash_on_delivery',
        has_fields: false,
        supports: {
          products: true,
          refunds: false,
          subscriptions: false,
          tokenization: false
        }
      },
      {
        id: 'bacs',
        title: this.language === 'ar' ? 'التحويل البنكي' : 'Bank Transfer',
        description: this.language === 'ar' ? 'حول المبلغ إلى حسابنا البنكي' : 'Transfer amount to our bank account',
        icon: '',
        order_button_text: this.language === 'ar' ? 'احجز وحول المبلغ' : 'Book and Transfer',
        type: 'bank_transfer',
        has_fields: false,
        supports: {
          products: true,
          refunds: true,
          subscriptions: false,
          tokenization: false
        }
      }
    ];

    const mockResponse: PaymentMethodsResponse = {
      success: true,
      payment_methods: mockMethods,
      total: mockMethods.length,
      country,
      order_total: orderTotal,
      currency: 'SAR',
      currency_symbol: 'ر.س',
      language: this.language,
      messages: {
        select_payment: this.language === 'ar' ? 'اختر طريقة الدفع' : 'Select Payment Method',
        payment_secure: this.language === 'ar' ? 'جميع المدفوعات آمنة ومحمية' : 'All payments are secure and protected',
        no_methods: this.language === 'ar' ? 'لا توجد طرق دفع متاحة' : 'No payment methods available',
        fees_may_apply: this.language === 'ar' ? 'قد تطبق رسوم إضافية' : 'Additional fees may apply',
        redirect_notice: this.language === 'ar' ? 'سيتم توجيهك لإتمام الدفع' : 'You will be redirected to complete payment'
      },
      timestamp: new Date().toISOString()
    };

    return {
      success: true,
      data: mockResponse,
      message: 'Payment methods loaded (local fallback)'
    };
  }
}

// Create singleton instance
const paymentService = new PaymentService();

export default paymentService;
